import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  XCircleIcon,
  useModalControls,
} from '@noor/ui';
import { getApiErrorMessage } from '@noor/api';
import { eatsKeys, providerImportApi, type ProviderImportJob } from '@noor/eats-api';
import type { ModalRenderProps } from '@noor/lib';
import { menuKeys } from '@/entities/menu';

const POLL_INTERVAL_MS = 2_000;

function isActive(job: ProviderImportJob | undefined): boolean {
  return (
    job === undefined ||
    job.status === 'IMPORT_JOB_STATUS_QUEUED' ||
    job.status === 'IMPORT_JOB_STATUS_RUNNING'
  );
}

function successfulCount(
  progress:
    | {
        created: number;
        already_existed: number;
      }
    | null
    | undefined,
): number {
  return (progress?.created ?? 0) + (progress?.already_existed ?? 0);
}

export function ProviderImportModal({
  payload,
}: ModalRenderProps<'provider-import-job'>) {
  const { t } = useTranslation('eats');
  const { open, close } = useModalControls();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshedJobRef = useRef<string>(undefined);

  const { data: job, error } = useQuery({
    queryKey: ['provider-import', 'jobs', payload.jobId],
    queryFn: ({ signal }) => providerImportApi.getJob(payload.jobId, { signal }),
    refetchInterval: (query) =>
      query.state.error || !isActive(query.state.data) ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const active = !error && isActive(job);
  const done = job?.status === 'IMPORT_JOB_STATUS_DONE';

  useEffect(() => {
    if (!job || isActive(job) || refreshedJobRef.current === job.job_id) return;
    refreshedJobRef.current = job.job_id;
    void queryClient.invalidateQueries({ queryKey: eatsKeys.restaurants.all });
    void queryClient.invalidateQueries({ queryKey: eatsKeys.promotions.all });
    // A completed import starts scrolling catalogs from page one instead of
    // refetching every previously loaded page.
    void queryClient.resetQueries({ queryKey: [...menuKeys.catalogs(), 'infinite'] });
    void queryClient.invalidateQueries({
      queryKey: menuKeys.catalogs(),
      predicate: (query) => query.queryKey[2] !== 'infinite',
    });
  }, [job, queryClient]);

  const stats = useMemo(() => {
    if (!job) return [];
    if (job.kind === 'IMPORT_JOB_KIND_RESTAURANTS') {
      return [
        {
          label: t('providerImport.entities.restaurants'),
          count: successfulCount(job.restaurants),
        },
      ];
    }
    if (job.kind === 'IMPORT_JOB_KIND_PROMOTIONS') {
      return [
        {
          label: t('providerImport.entities.promotions'),
          count: successfulCount(job.promotions),
        },
      ];
    }
    return [
      {
        label: t('providerImport.entities.categories'),
        count: successfulCount(job.categories),
      },
      {
        label: t('providerImport.entities.products'),
        count: successfulCount(job.products),
      },
      {
        label: t('providerImport.entities.modifierGroups'),
        count: successfulCount(job.modifier_groups),
      },
      {
        label: t('providerImport.entities.modifierOptions'),
        count: successfulCount(job.modifier_options),
      },
    ];
  }, [job, t]);

  const failedItems = Math.max(
    job?.failed_items ?? 0,
    job?.failed_count ?? 0,
    job?.failures.length ?? 0,
  );
  const failedEntities = [
    job?.restaurants,
    job?.categories,
    job?.promotions,
    job?.products,
    job?.modifier_groups,
    job?.modifier_options,
  ].reduce((total, progress) => total + (progress?.failed ?? 0), 0);
  const issueCount = Math.max(failedItems, failedEntities);
  const hasPartialFailures = done && (issueCount > 0 || (job?.failures.length ?? 0) > 0);

  function goHome(): void {
    close();
    void navigate({ to: '/eats' });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !active) close();
      }}
    >
      <DialogContent
        showClose={false}
        focusOnOpen={false}
        {...(active
          ? {
              onInteractOutside: (event) => event.preventDefault(),
              onEscapeKeyDown: (event) => event.preventDefault(),
            }
          : {})}
        className="flex max-w-[30rem] flex-col items-center gap-8 rounded-3xl border-0 p-6"
      >
        {active ? (
          <div className="flex w-full flex-col items-center gap-6 px-4 sm:px-[2.625rem]">
            <div className="size-20 overflow-hidden" aria-hidden="true">
              <img
                src="/provider-import/waiting.gif"
                alt=""
                className="-m-2.5 size-[6.25rem] max-w-none object-cover"
              />
            </div>
            <div className="flex w-full flex-col items-center gap-2 text-center">
              <DialogTitle className="text-[1.375rem] leading-none font-semibold tracking-[-0.04em] text-foreground">
                {t('providerImport.waiting.title')}
              </DialogTitle>
              <DialogDescription className="text-sm leading-none font-medium tracking-[-0.02em] text-muted-foreground">
                {t('providerImport.waiting.description')}
              </DialogDescription>
            </div>
          </div>
        ) : done ? (
          <>
            <div className="flex w-full flex-col items-center gap-6 px-4 sm:px-[2.625rem]">
              <img
                src="/provider-import/success.png"
                alt=""
                aria-hidden="true"
                className="size-16 object-cover"
              />
              <div className="flex w-full flex-col items-center gap-2 text-center">
                <DialogTitle className="text-[1.375rem] leading-none font-semibold tracking-[-0.04em] text-foreground">
                  {t(
                    hasPartialFailures
                      ? 'providerImport.done.partialTitle'
                      : 'providerImport.done.title',
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('providerImport.done.description')}
                </DialogDescription>
                <div className="text-sm leading-[1.4] font-medium tracking-[-0.02em] text-muted-foreground">
                  {stats.map(({ label, count }) => (
                    <p key={label}>
                      {label} - {t('providerImport.items', { count })}
                    </p>
                  ))}
                </div>
                {hasPartialFailures ? (
                  <div className="mt-2 w-full rounded-xl bg-destructive/10 p-3 text-left text-sm text-destructive">
                    <p className="font-semibold">
                      {t('providerImport.done.failedItems', { count: issueCount })}
                    </p>
                    {job.failures.map((failure) => (
                      <p
                        key={failure.restaurant_id || failure.external_id}
                        className="mt-1"
                      >
                        {failure.restaurant_name || failure.external_id}
                        {failure.reason ? ` — ${failure.reason}` : ''}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <ResultButton onClick={goHome}>{t('providerImport.home')}</ResultButton>
          </>
        ) : (
          <>
            <div className="flex w-full flex-col items-center gap-6 px-4 sm:px-[2.625rem]">
              <XCircleIcon className="size-16" />
              <div className="flex w-full flex-col items-center gap-2 text-center">
                <DialogTitle className="text-[1.375rem] leading-none font-semibold tracking-[-0.04em] text-foreground">
                  {t('providerImport.failed.title')}
                </DialogTitle>
                <DialogDescription className="text-sm leading-[1.4] font-medium tracking-[-0.02em] text-muted-foreground">
                  {job?.status === 'IMPORT_JOB_STATUS_STALE'
                    ? t('providerImport.failed.stale')
                    : (job?.error ??
                      (error
                        ? getApiErrorMessage(
                            error,
                            t('providerImport.failed.description'),
                          )
                        : t('providerImport.failed.description')))}
                </DialogDescription>
              </div>
            </div>
            <ResultButton onClick={goHome}>{t('providerImport.home')}</ResultButton>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-center justify-center rounded-full bg-primary p-4 text-base font-semibold tracking-[-0.04em] text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
    >
      {children}
    </button>
  );
}
